import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { CalendarDay } from 'ember-power-calendar/components/power-calendar';
import PowerCalendar from 'ember-power-calendar/components/power-calendar';
import formatDate from 'ember-power-calendar/helpers/power-calendar-format-date';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import dayjsToDate from 'lumen/helpers/dayjs-to-date';
import hasFeature from 'lumen/helpers/has-feature';

interface GeneralDatepickersShopDatePickerSignature {
  Args: {
    extractDate: string;
    minDate?: Dayjs;
    onSelect: (date: string) => void;
  };
  Blocks: {
    default: [];
  };
}

export default class GeneralDatepickersShopDatePicker extends Component<GeneralDatepickersShopDatePickerSignature> {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @tracked calendarAnchor: Dayjs | null = null;
  @tracked isOpen = false;

  get currentCalendarAnchor(): Dayjs {
    return (
      this.calendarAnchor ??
      (this.args.extractDate ? dayjs(this.args.extractDate) : null) ??
      this.hotelAndMarketService.getLocalTime()
    );
  }

  get maxShopDate(): Dayjs {
    return this.hotelAndMarketService.getLocalTime();
  }

  get monthFormat(): string {
    return this.dates.partialDateFormat?.YEAR_AND_MONTH_LONG ?? 'MMMM YYYY';
  }

  get selectedDate(): Date | undefined {
    return this.args.extractDate
      ? dayjs(this.args.extractDate).toDate()
      : undefined;
  }

  @action
  nextMonth(): void {
    this.calendarAnchor = this.currentCalendarAnchor.add(1, 'month');
  }

  @action
  previousMonth(): void {
    this.calendarAnchor = this.currentCalendarAnchor.subtract(1, 'month');
  }

  @action
  selectDate(day: CalendarDay): void {
    const picked = (day as { moment: Dayjs }).moment;
    const isToday = picked.isSame(
      this.hotelAndMarketService.getLocalTime(),
      'day',
    );
    this.args.onSelect(isToday ? '' : picked.format('YYYY-MM-DD'));
    this.isOpen = false;
  }

  @action
  toggle(): void {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.calendarAnchor = null;
    }
  }

  <template>
    {{#if (hasFeature "user.staff")}}
      <div class="relative">
        <button
          class="font-inherit m-0 cursor-pointer border-0 bg-transparent p-0 text-inherit hover:opacity-70"
          type="button"
          {{on "click" this.toggle}}
        >
          {{yield}}
        </button>
        {{#if this.isOpen}}
          {{! Click-trap: invisible overlay catches outside clicks }}
          {{! aria-hidden: purely a visual click-trap, not meaningful to screen readers }}
          <button
            aria-hidden="true"
            class="fixed inset-0 z-[1198] cursor-default border-0 bg-transparent p-0"
            tabindex="-1"
            type="button"
            {{on "click" this.toggle}}
          />
          <div class="rounded-dropdown right-0">
            <div class="relative flex flex-row">
              <button
                class="calendar-button"
                type="button"
                {{on "click" this.previousMonth}}
              >
                <div class="calendar-button-icon">
                  <PrismIcon @color="inherit" @name="chevron-left" />
                </div>
              </button>
              <PowerCalendar
                @center={{dayjsToDate this.currentCalendarAnchor}}
                @onSelect={{this.selectDate}}
                @selected={{this.selectedDate}}
                class="demo-calendar-small"
                as |calendar|
              >
                <calendar.Nav>
                  <div class="month-title flex items-center justify-center">
                    {{formatDate
                      calendar.center
                      this.monthFormat
                      locale=calendar.locale
                    }}
                  </div>
                </calendar.Nav>
                <calendar.Days
                  @maxDate={{dayjsToDate this.maxShopDate}}
                  @minDate={{if @minDate (dayjsToDate @minDate)}}
                  @startOfWeek={{this.dates.startOfWeek}}
                  @weekdayFormat="min"
                />
              </PowerCalendar>
              <button
                class="calendar-button"
                type="button"
                {{on "click" this.nextMonth}}
              >
                <div class="calendar-button-icon">
                  <PrismIcon @color="inherit" @name="chevron-right" />
                </div>
              </button>
              <div class="absolute top-200 right-200">
                <GeneralIconWithTooltip
                  @side="left"
                  @tooltipContent="[Staff] View data as of a historical shop date"
                />
              </div>
            </div>
          </div>
        {{/if}}
      </div>
    {{else}}
      {{yield}}
    {{/if}}
  </template>
}
